import { useEffect, useState } from 'react';
import {
  convertToAlumni,
  finalizeFnFSettlement,
  listAlumni,
  listClearanceTasks,
  listGratuityRecords,
  listGratuityRules,
  listResignations,
  reviewResignation,
  signoffClearanceTask,
  submitExitInterview,
  submitResignation,
  type AlumniRecord,
  type ClearanceTask,
  type FnfSettlement,
  type GratuityRecord,
  type GratuityRule,
  type Resignation,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import { Icon } from '../components/Icon';
import { StatusBadge } from '../components/StatusBadge';

export function Exit() {
  const { can } = useAuth();
  const { formatMoney } = useLocalization();
  const [tab, setTab] = useState<'resignations' | 'fnf' | 'interviews' | 'gratuity' | 'alumni'>('resignations');
  const [resignations, setResignations] = useState<Resignation[]>([]);
  const [gratuityRules, setGratuityRules] = useState<GratuityRule[]>([]);
  const [gratuityRecords, setGratuityRecords] = useState<GratuityRecord[]>([]);
  const [alumni, setAlumni] = useState<AlumniRecord[]>([]);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 1. Resignation Submit Modal
  const [resModal, setResModal] = useState(false);
  const [lastDate, setLastDate] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [resReason, setResReason] = useState('');

  // 2. Review Modal
  const [reviewModal, setReviewModal] = useState(false);
  const [selectedRes, setSelectedRes] = useState<Resignation | null>(null);
  const [reviewStatus, setReviewStatus] = useState<'approved' | 'rejected'>('approved');
  const [officialDate, setOfficialDate] = useState('');

  // 3. Clearances Modal
  const [clearanceModal, setClearanceModal] = useState(false);
  const [tasks, setTasks] = useState<ClearanceTask[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // 4. FnF Modal
  const [fnfModal, setFnfModal] = useState(false);
  const [fnfData, setFnfData] = useState<FnfSettlement | null>(null);
  const [loadingFnf, setLoadingFnf] = useState(false);

  // 5. Exit Interview Modal
  const [interviewModal, setInterviewModal] = useState(false);
  const [selectedResForInterview, setSelectedResForInterview] = useState<Resignation | null>(null);
  const [exitReason, setExitReason] = useState('Career advancement & higher compensation opportunity');
  const [jobSatRating, setJobSatRating] = useState(4);
  const [mgmtRating, setMgmtRating] = useState(4);
  const [cultureRating, setCultureRating] = useState(5);
  const [whatWentWell, setWhatWentWell] = useState('Great team culture, challenging projects, collaborative environment.');
  const [whatCouldImprove, setWhatCouldImprove] = useState('More fast-tracked promotions and structured technical learning paths.');
  const [leadershipFeedback, setLeadershipFeedback] = useState('Overall positive experience, grateful for the opportunities provided.');

  // 6. Convert to Alumni Modal
  const [alumniModal, setAlumniModal] = useState(false);
  const [selectedResForAlumni, setSelectedResForAlumni] = useState<Resignation | null>(null);
  const [personalEmail, setPersonalEmail] = useState('');
  const [personalPhone, setPersonalPhone] = useState('');
  const [rehireEligibility, setRehireEligibility] = useState<'eligible' | 'conditional' | 'ineligible'>('eligible');
  const [rehireNotes, setRehireNotes] = useState('Eligible for future senior rehire.');

  async function loadData() {
    try {
      const [resRes, rulesRes, gratRes, alumniRes] = await Promise.all([
        listResignations({ viewAll: can('organization.manage') || can('employee.edit') }).catch(() => ({ data: [] })),
        listGratuityRules().catch(() => ({ data: [] })),
        listGratuityRecords().catch(() => ({ data: [] })),
        listAlumni().catch(() => ({ data: [] })),
      ]);
      setResignations(resRes.data || []);
      setGratuityRules(rulesRes.data || []);
      setGratuityRecords(gratRes.data || []);
      setAlumni(alumniRes.data || []);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load exit records' });
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  async function handleSubmitResignation(e: React.FormEvent) {
    e.preventDefault();
    try {
      await submitResignation({
        requestedLastWorkingDate: lastDate || '2026-09-30',
        reason: resReason,
      });
      setMsg({ type: 'success', text: 'Resignation submitted successfully to HR.' });
      setResModal(false);
      setResReason('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to submit resignation' });
    }
  }

  async function handleReviewResignation(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedRes) return;
    try {
      await reviewResignation(selectedRes.id, {
        status: reviewStatus,
        officialLastDate: officialDate || selectedRes.requested_last_working_date,
      });
      setMsg({ type: 'success', text: `Resignation ${reviewStatus.toUpperCase()} successfully!` });
      setReviewModal(false);
      setSelectedRes(null);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to review resignation' });
    }
  }

  async function openClearances(res: Resignation) {
    setSelectedRes(res);
    setClearanceModal(true);
    try {
      setLoadingTasks(true);
      const data = await listClearanceTasks(res.id);
      setTasks(data.data || []);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load clearance tasks' });
    } finally {
      setLoadingTasks(false);
    }
  }

  async function handleTaskSignoff(taskId: string, status: 'cleared' | 'flagged') {
    try {
      await signoffClearanceTask(taskId, { status, remarks: `Status updated to ${status}` });
      setMsg({ type: 'success', text: `Task signed off as ${status.toUpperCase()}` });
      if (selectedRes) {
        const data = await listClearanceTasks(selectedRes.id);
        setTasks(data.data || []);
      }
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to sign off task' });
    }
  }

  async function openFnF(res: Resignation) {
    setSelectedRes(res);
    setFnfModal(true);
    try {
      setLoadingFnf(true);
      const resData = await finalizeFnFSettlement(res.id);
      setFnfData(resData.data);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to compute settlement' });
    } finally {
      setLoadingFnf(false);
    }
  }

  async function handleSubmitExitInterview(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedResForInterview) return;
    try {
      await submitExitInterview(selectedResForInterview.id, {
        primaryReasonForLeaving: exitReason,
        jobSatisfactionRating: jobSatRating,
        managementSupportRating: mgmtRating,
        cultureRating: cultureRating,
        whatWentWell,
        whatCouldImprove,
        recommendOrganization: true,
        feedbackToLeadership: leadershipFeedback,
      });
      setMsg({ type: 'success', text: 'Exit interview feedback recorded confidentially!' });
      setInterviewModal(false);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to submit exit interview' });
    }
  }

  async function handleConvertToAlumni(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedResForAlumni) return;
    try {
      await convertToAlumni({
        employeeId: selectedResForAlumni.employee_id,
        personalEmail: personalEmail || undefined,
        personalPhone: personalPhone || undefined,
        rehireEligibility,
        rehireNotes,
      });
      setMsg({
        type: 'success',
        text: `Employee converted to Alumni directory! System access deactivated; historical records preserved.`,
      });
      setAlumniModal(false);
      await loadData();
      setTab('alumni');
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to convert to alumni' });
    }
  }

  const isHr = can('organization.manage') || can('employee.edit');

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Separation, Clearances & Alumni Management</div>
          <h1>Employee Offboarding, Gratuity & Alumni Hub</h1>
        </div>
        <div className="toolbar">
          <button className="btn" onClick={() => setResModal(true)}>
            <Icon name="exit" /> Submit Resignation
          </button>
        </div>
      </div>

      {msg && (
        <div className={`alert ${msg.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <Icon name={msg.type === 'success' ? 'check' : 'alertCircle'} />
          <span>{msg.text}</span>
          <button className="btn-quiet" style={{ marginLeft: 'auto' }} onClick={() => setMsg(null)}>✕</button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-top">
            <div className="kpi-label">Active Resignations</div>
            <div className="kpi-icon-badge" style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>
              <Icon name="exit" size={18} />
            </div>
          </div>
          <div className="kpi-value">{resignations.filter((r) => r.status === 'submitted' || r.status === 'approved').length}</div>
          <div className="kpi-hint">Under Notice Period</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-top">
            <div className="kpi-label">Statutory Gratuity Records</div>
            <div className="kpi-icon-badge" style={{ background: 'rgba(99,102,241,0.12)', color: '#6366f1' }}>
              <Icon name="lock" size={18} />
            </div>
          </div>
          <div className="kpi-value">{gratuityRecords.length}</div>
          <div className="kpi-hint">Formula: (15 × Basic × Tenure) / 26</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-top">
            <div className="kpi-label">Alumni Directory</div>
            <div className="kpi-icon-badge" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>
              <Icon name="users" size={18} />
            </div>
          </div>
          <div className="kpi-value" style={{ color: '#10b981' }}>{alumni.length}</div>
          <div className="kpi-hint">Preserved Historical Records</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-top">
            <div className="kpi-label">Active Gratuity Rules</div>
            <div className="kpi-icon-badge" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>
              <Icon name="scale" size={18} />
            </div>
          </div>
          <div className="kpi-value">{gratuityRules.filter((r) => r.is_active).length}</div>
          <div className="kpi-hint">Versioned Policy Engine</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="tabs" style={{ marginTop: 24, marginBottom: 20 }}>
        <button className={`tab-btn ${tab === 'resignations' ? 'active' : ''}`} onClick={() => setTab('resignations')}>
          <Icon name="exit" /> Resignations & Clearances ({resignations.length})
        </button>
        <button className={`tab-btn ${tab === 'fnf' ? 'active' : ''}`} onClick={() => setTab('fnf')}>
          <Icon name="finance" /> Full & Final Settlement (F&F)
        </button>
        <button className={`tab-btn ${tab === 'interviews' ? 'active' : ''}`} onClick={() => setTab('interviews')}>
          <Icon name="employees" /> Exit Interviews
        </button>
        <button className={`tab-btn ${tab === 'gratuity' ? 'active' : ''}`} onClick={() => setTab('gratuity')}>
          <Icon name="lock" /> Statutory Gratuity Engine ({gratuityRules.length})
        </button>
        <button className={`tab-btn ${tab === 'alumni' ? 'active' : ''}`} onClick={() => setTab('alumni')}>
          <Icon name="award" /> Alumni Directory ({alumni.length})
        </button>
      </div>

      {/* TAB 1: RESIGNATIONS & CLEARANCES */}
      {tab === 'resignations' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Resignation Ledger & Departmental Clearances</h2>
            <span className="muted" style={{ fontSize: 13 }}>{resignations.length} Separation Requests</span>
          </div>
          {resignations.length === 0 ? (
            <div className="empty">No resignations submitted.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Designation</th>
                  <th>Submitted Date</th>
                  <th>Requested Last Day</th>
                  <th>Official Last Day</th>
                  <th>Status</th>
                  <th>Lifecycle Actions</th>
                </tr>
              </thead>
              <tbody>
                {resignations.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.first_name} {r.last_name}</div>
                      <div className="mono muted" style={{ fontSize: 11 }}>{r.employee_code}</div>
                    </td>
                    <td>{r.designation || 'Staff'}</td>
                    <td className="mono">{r.submission_date}</td>
                    <td className="mono">{r.requested_last_working_date}</td>
                    <td className="mono">{r.official_last_working_date || '—'}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {r.status === 'submitted' && isHr && (
                          <button
                            className="btn-quiet"
                            onClick={() => {
                              setSelectedRes(r);
                              setOfficialDate(r.requested_last_working_date);
                              setReviewModal(true);
                            }}
                          >
                            Review & Notice
                          </button>
                        )}
                        {r.status === 'approved' && (
                          <>
                            <button className="btn-quiet" onClick={() => void openClearances(r)}>
                              Clearance Tasks
                            </button>
                            <button className="btn-quiet" onClick={() => void openFnF(r)}>
                              F&F Statement
                            </button>
                            <button
                              className="btn-quiet"
                              onClick={() => {
                                setSelectedResForInterview(r);
                                setInterviewModal(true);
                              }}
                            >
                              Exit Interview
                            </button>
                            {isHr && (
                              <button
                                className="btn"
                                style={{ padding: '3px 8px', fontSize: 12, background: '#22c55e' }}
                                onClick={() => {
                                  setSelectedResForAlumni(r);
                                  setAlumniModal(true);
                                }}
                              >
                                Convert to Alumni
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 2: FULL & FINAL (FNF) SETTLEMENTS */}
      {tab === 'fnf' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Full & Final (F&F) Settlement Statements</h2>
            <span className="muted" style={{ fontSize: 13 }}>Final accounting breakdown</span>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Separation Status</th>
                <th>Official Last Day</th>
                <th>F&F Action</th>
              </tr>
            </thead>
            <tbody>
              {resignations.filter((r) => r.status === 'approved' || r.status === 'completed').map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 600 }}>{r.first_name} {r.last_name} ({r.employee_code})</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td className="mono">{r.official_last_working_date}</td>
                  <td>
                    <button className="btn-quiet" onClick={() => void openFnF(r)}>
                      View & Approve F&F Payout &rarr;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 3: EXIT INTERVIEWS */}
      {tab === 'interviews' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Exit Feedback & Insights</h2>
            <span className="muted" style={{ fontSize: 13 }}>Confidential organizational sentiment</span>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Separation Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {resignations.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 600 }}>{r.first_name} {r.last_name} ({r.employee_code})</td>
                  <td>{r.reason || 'Not specified'}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>
                    <button
                      className="btn-quiet"
                      onClick={() => {
                        setSelectedResForInterview(r);
                        setInterviewModal(true);
                      }}
                    >
                      Fill / View Survey &rarr;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 4: STATUTORY GRATUITY RULE ENGINE */}
      {tab === 'gratuity' && (
        <div>
          <div className="panel" style={{ marginBottom: 20 }}>
            <div className="panel-head">
              <h2>Statutory Gratuity Rules & Jurisdictions</h2>
              <span className="muted" style={{ fontSize: 13 }}>Versioned calculation rules</span>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Rule Code</th>
                  <th>Jurisdiction & Policy</th>
                  <th>Min Qualifying Years</th>
                  <th>Formula Expression</th>
                  <th>Statutory Ceiling</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {gratuityRules.map((rule) => (
                  <tr key={rule.id}>
                    <td className="mono" style={{ fontWeight: 600 }}>{rule.rule_code}</td>
                    <td>
                      <div><strong>{rule.rule_name}</strong></div>
                      <div className="muted" style={{ fontSize: 12 }}>{rule.jurisdiction} • Effective {rule.effective_from}</div>
                    </td>
                    <td><strong>{rule.min_service_years} Years</strong></td>
                    <td className="mono" style={{ color: '#93c5fd' }}>{rule.formula_expression}</td>
                    <td className="mono money" style={{ color: '#22c55e' }}>{formatMoney(rule.max_statutory_ceiling)}</td>
                    <td>
                      {rule.is_active ? (
                        <span className="pill" style={{ background: '#22c55e', color: '#fff' }}>Active Statutory Policy</span>
                      ) : (
                        <span className="muted">Archived</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="panel">
            <div className="panel-head">
              <h2>Historical Gratuity Determinations & Calculations ({gratuityRecords.length})</h2>
            </div>
            {gratuityRecords.length === 0 ? (
              <div className="empty">No gratuity records calculated yet.</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Continuous Tenure</th>
                    <th>Qualifying Years</th>
                    <th>Last Basic</th>
                    <th>Estimated Gratuity</th>
                    <th>Payment Status</th>
                    <th>Legal Determination Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {gratuityRecords.map((g) => (
                    <tr key={g.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{g.first_name} {g.last_name}</div>
                        <div className="mono muted" style={{ fontSize: 11 }}>{g.employee_code}</div>
                      </td>
                      <td>{g.total_service_years} Yrs</td>
                      <td><strong>{g.qualifying_service_years} Yrs</strong></td>
                      <td className="mono">{formatMoney(g.last_drawn_basic)}</td>
                      <td className="mono money" style={{ color: '#22c55e', fontWeight: 600 }}>
                        {formatMoney(g.estimated_gratuity_amount)}
                      </td>
                      <td><StatusBadge status={g.payment_status} /></td>
                      <td style={{ fontSize: 12, maxWidth: 260 }}>{g.legal_determination_notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* TAB 5: ALUMNI DIRECTORY */}
      {tab === 'alumni' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Alumni Network & Former Employee Directory</h2>
            <span className="muted" style={{ fontSize: 13 }}>Preserved employment archives</span>
          </div>
          {alumni.length === 0 ? (
            <div className="empty">No alumni records found.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Alumni Member</th>
                  <th>Last Designation & Dept</th>
                  <th>Tenure</th>
                  <th>Contact Info</th>
                  <th>Rehire Eligibility</th>
                  <th>Certificates</th>
                </tr>
              </thead>
              <tbody>
                {alumni.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{a.full_name}</div>
                      <div className="mono muted" style={{ fontSize: 11 }}>{a.employee_code}</div>
                    </td>
                    <td>
                      <div>{a.last_designation}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{a.last_department_name || 'General'}</div>
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {a.date_of_joining} &rarr; {a.date_of_exit}
                    </td>
                    <td>
                      <div>{a.personal_email || '—'}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{a.personal_phone || '—'}</div>
                    </td>
                    <td>
                      <span
                        className="pill"
                        style={{
                          background: a.rehire_eligibility === 'eligible' ? '#22c55e' : '#eab308',
                          color: '#fff',
                          textTransform: 'uppercase',
                        }}
                      >
                        {a.rehire_eligibility}
                      </span>
                    </td>
                    <td>
                      <span className="pill" style={{ background: '#1e293b' }}>
                        Relieving & Experience Verified
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* MODAL 1: SUBMIT RESIGNATION */}
      {resModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-head">
              <h2>Submit Resignation Notice</h2>
              <button className="btn-quiet" onClick={() => setResModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmitResignation}>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Requested Last Working Date</label>
                <input
                  className="input"
                  type="date"
                  value={lastDate}
                  onChange={(e) => setLastDate(e.target.value)}
                  required
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Reason for Separation</label>
                <textarea
                  className="input"
                  rows={3}
                  value={resReason}
                  onChange={(e) => setResReason(e.target.value)}
                  required
                  placeholder="State your reason for separation..."
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setResModal(false)}>Cancel</button>
                <button type="submit" className="btn">Submit Notice</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: REVIEW RESIGNATION */}
      {reviewModal && selectedRes && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-head">
              <h2>Review Resignation & Notice Period</h2>
              <button className="btn-quiet" onClick={() => setReviewModal(false)}>✕</button>
            </div>
            <form onSubmit={handleReviewResignation}>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Employee</label>
                <div style={{ fontWeight: 600 }}>{selectedRes.first_name} {selectedRes.last_name} ({selectedRes.employee_code})</div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Decision</label>
                <select
                  className="input"
                  value={reviewStatus}
                  onChange={(e: any) => setReviewStatus(e.target.value)}
                >
                  <option value="approved">Approve Resignation & Initiate Clearances</option>
                  <option value="rejected">Reject / Retain Employee</option>
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Official Last Working Date</label>
                <input
                  className="input"
                  type="date"
                  value={officialDate}
                  onChange={(e) => setOfficialDate(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setReviewModal(false)}>Cancel</button>
                <button type="submit" className="btn">Confirm Notice Decision</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: CLEARANCE TASKS */}
      {clearanceModal && selectedRes && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 640 }}>
            <div className="modal-head">
              <h2>Clearance Checklist · {selectedRes.first_name} {selectedRes.last_name}</h2>
              <button className="btn-quiet" onClick={() => setClearanceModal(false)}>✕</button>
            </div>
            {loadingTasks ? (
              <div className="empty">Loading tasks...</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Task Item</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t) => (
                    <tr key={t.id}>
                      <td><strong>{t.department_name}</strong></td>
                      <td>{t.task_name}</td>
                      <td><StatusBadge status={t.status} /></td>
                      <td>
                        {t.status === 'pending' ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn" style={{ padding: '3px 8px', fontSize: 12, background: '#22c55e' }} onClick={() => void handleTaskSignoff(t.id, 'cleared')}>
                              Sign Off
                            </button>
                            <button className="btn-quiet" style={{ padding: '3px 8px', fontSize: 12, color: '#ef4444' }} onClick={() => void handleTaskSignoff(t.id, 'flagged')}>
                              Flag
                            </button>
                          </div>
                        ) : (
                          <span className="mono muted" style={{ fontSize: 12 }}>Cleared</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn-quiet" onClick={() => setClearanceModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 4: FNF STATEMENT */}
      {fnfModal && selectedRes && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 620 }}>
            <div className="modal-head">
              <h2>Full & Final (F&F) Statement · {selectedRes.first_name} {selectedRes.last_name}</h2>
              <button className="btn-quiet" onClick={() => setFnfModal(false)}>✕</button>
            </div>
            {loadingFnf || !fnfData ? (
              <div className="empty">Computing settlement amounts...</div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  {/* Earnings */}
                  <div className="panel" style={{ padding: 12 }}>
                    <h3 style={{ fontSize: 13, textTransform: 'uppercase', marginBottom: 8, color: '#22c55e' }}>Earnings & Gratuity</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                      <span>Gross Payable</span>
                      <span className="mono">{formatMoney(fnfData.gross_payable || 0)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                      <span>Earned Leave Encashment</span>
                      <span className="mono">{formatMoney(fnfData.leave_encashment_amount || 0)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                      <span>Statutory Gratuity</span>
                      <span className="mono">{formatMoney(fnfData.gratuity_amount || 0)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                      <span>Total Earnings</span>
                      <span className="mono" style={{ color: '#22c55e' }}>{formatMoney(fnfData.total_earnings || 0)}</span>
                    </div>
                  </div>

                  {/* Deductions */}
                  <div className="panel" style={{ padding: 12 }}>
                    <h3 style={{ fontSize: 13, textTransform: 'uppercase', marginBottom: 8, color: '#ef4444' }}>Recoveries & Deductions</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                      <span>Loan Advance Recovery</span>
                      <span className="mono">{formatMoney(fnfData.loan_recovery_amount || 0)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                      <span>Notice Shortfall</span>
                      <span className="mono">{formatMoney(fnfData.notice_period_shortfall_deduction || 0)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                      <span>Asset Non-return / Damage</span>
                      <span className="mono">{formatMoney(fnfData.asset_damage_deduction || 0)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                      <span>Total Deductions</span>
                      <span className="mono" style={{ color: '#ef4444' }}>{formatMoney(fnfData.total_deductions || 0)}</span>
                    </div>
                  </div>
                </div>

                {/* Net Disbursable */}
                <div
                  style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: 8,
                    padding: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div className="muted" style={{ fontSize: 12 }}>Net Final Settlement Amount</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#4ade80' }}>
                      {formatMoney(fnfData.net_settlement_amount || 0)}
                    </div>
                  </div>
                  <button
                    className="btn"
                    style={{ background: '#22c55e' }}
                    onClick={() => {
                      setMsg({ type: 'success', text: 'Full & Final settlement approved and queued for payout!' });
                      setFnfModal(false);
                    }}
                  >
                    Approve F&F Settlement
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL 5: EXIT INTERVIEW */}
      {interviewModal && selectedResForInterview && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 540 }}>
            <div className="modal-head">
              <h2>Exit Interview Survey · {selectedResForInterview.first_name} {selectedResForInterview.last_name}</h2>
              <button className="btn-quiet" onClick={() => setInterviewModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmitExitInterview}>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Primary Reason for Leaving</label>
                <input
                  className="input"
                  value={exitReason}
                  onChange={(e) => setExitReason(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Job Sat (1-5)</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={5}
                    value={jobSatRating}
                    onChange={(e) => setJobSatRating(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="label">Management (1-5)</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={5}
                    value={mgmtRating}
                    onChange={(e) => setMgmtRating(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="label">Culture (1-5)</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={5}
                    value={cultureRating}
                    onChange={(e) => setCultureRating(Number(e.target.value))}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">What went well during your tenure?</label>
                <textarea
                  className="input"
                  rows={2}
                  value={whatWentWell}
                  onChange={(e) => setWhatWentWell(e.target.value)}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Suggestions for Leadership & Improvement</label>
                <textarea
                  className="input"
                  rows={2}
                  value={whatCouldImprove}
                  onChange={(e) => setWhatCouldImprove(e.target.value)}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Message to Leadership & Founders</label>
                <textarea
                  className="input"
                  rows={2}
                  value={leadershipFeedback}
                  onChange={(e) => setLeadershipFeedback(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setInterviewModal(false)}>Cancel</button>
                <button type="submit" className="btn">Submit Confidential Survey</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 6: CONVERT TO ALUMNI */}
      {alumniModal && selectedResForAlumni && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-head">
              <h2>Archive to Alumni Directory</h2>
              <button className="btn-quiet" onClick={() => setAlumniModal(false)}>✕</button>
            </div>
            <form onSubmit={handleConvertToAlumni}>
              <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
                Transfers {selectedResForAlumni.first_name} {selectedResForAlumni.last_name} into the Alumni directory. System login credentials will be deactivated while preserving all historical records.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Personal Email</label>
                  <input
                    className="input"
                    type="email"
                    value={personalEmail}
                    onChange={(e) => setPersonalEmail(e.target.value)}
                    placeholder="alumni@gmail.com"
                  />
                </div>
                <div>
                  <label className="label">Personal Phone</label>
                  <input
                    className="input"
                    value={personalPhone}
                    onChange={(e) => setPersonalPhone(e.target.value)}
                    placeholder="+91..."
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Rehire Eligibility</label>
                <select
                  className="input"
                  value={rehireEligibility}
                  onChange={(e: any) => setRehireEligibility(e.target.value)}
                >
                  <option value="eligible">Eligible for Rehire (Recommended)</option>
                  <option value="conditional">Conditional Review Required</option>
                  <option value="ineligible">Ineligible for Rehire</option>
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Rehire Notes</label>
                <textarea
                  className="input"
                  rows={2}
                  value={rehireNotes}
                  onChange={(e) => setRehireNotes(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setAlumniModal(false)}>Cancel</button>
                <button type="submit" className="btn" style={{ background: '#22c55e' }}>Confirm Alumni Conversion</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
